import DOMPurify from "dompurify";
import { motion } from "framer-motion";
import React, { useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Assets } from "../../constants/assets";
import useHoverSfx from "../../hooks/useHoverSfx";
import { setUserInfo } from "../../redux/user/action";
import Network, { ProfileURL } from "../../utils/Network";
import ThousandsSeparator from "../../utils/thousands-separator";
import CircularProgress from "../CircularProgress";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import ResourceCoachMark from "./ResourceCoachMark";
import ResourcesDialog from "./ResourcesDialog";
import "./map-hud.css";

type Props = {
  toggleDrawer: Function;
  handleLogout?: Function;
};

/** Compact resource value: 1234 -> 1.2k, 2_500_000 -> 2.5m */
const compact = (value: any): string => {
  const n = Number(value) || 0;
  if (n >= 1_000_000)
    return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, "") + "m";
  if (n >= 1_000)
    return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1).replace(/\.0$/, "") + "k";
  return Math.floor(n).toString();
};

const MapTopBar = ({ toggleDrawer }: Props) => {
  const { t } = useAppTranslation(["navigation", "common"]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const axios = new Network();
  const userInfo = useSelector((state: any) => state.userInfo);
  const [notifLoading, setNotifLoading] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const hoverSfx = useHoverSfx();

  // Open the profile / settings dialog directly (the layouts show it whenever
  // the URL hash contains "settings"), matching the drawer's Settings link.
  const openSettings = () =>
    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: "#settings",
    });

  const resources = useMemo(
    () => [
      // Cleg carries the gold treatment — the design calls it out as the
      // headline currency.
      { key: "cleg", label: "common:resources.cleg", icon: "/assets/images/icons/cleg.png", value: userInfo?.totalCleg, gold: true },
      { key: "energy", label: "common:resources.energy", icon: Assets.icons.energy, value: userInfo?.totalEnergy },
      { key: "stone", label: "common:resources.stone", icon: "/assets/images/icons/stone.png", value: userInfo?.totalStone },
      { key: "iron", label: "common:resources.iron", icon: "/assets/images/icons/metal.png", value: userInfo?.totalIron },
      { key: "blood", label: "common:resources.bloodGem", icon: Assets.icons.bloodStone, value: userInfo?.totalBloodGem },
      { key: "ember", label: "common:resources.ember", icon: Assets.icons.ember1x1, value: userInfo?.totalEmber },
    ],
    [userInfo]
  );

  // The whole bar opens the wallet ("click anywhere" in the coach mark), but the
  // strip also scrolls horizontally on mobile — so ignore clicks that were
  // really the end of a drag.
  const dragGuard = useRef({ x: 0, y: 0, moved: false });
  const onBarPointerDown = (e: React.PointerEvent) => {
    dragGuard.current = { x: e.clientX, y: e.clientY, moved: false };
  };
  const onBarPointerMove = (e: React.PointerEvent) => {
    const d = dragGuard.current;
    if (Math.abs(e.clientX - d.x) > 6 || Math.abs(e.clientY - d.y) > 6) d.moved = true;
  };
  const onBarClick = () => {
    if (dragGuard.current.moved) return;
    setResourcesOpen(true);
  };

  const unreadNotifications = userInfo?.unreadNotifications;
  const isThereNotif = useMemo(() => Boolean(userInfo?.newNotification), [userInfo]);

  const getMe = async () => {
    const response = await axios.CallApi({ url: "/player", method: "get" });
    if (response.ok) dispatch(setUserInfo(response.data));
  };

  const markAsRead = async (id: number, redirect?: boolean) => {
    if (!id) return;
    setNotifLoading(true);
    await axios.CallApi({ url: "/notification/read", method: "post", body: [id] });
    if (redirect && userInfo?.newNotification?.url) {
      window.location.href = userInfo.newNotification.url;
      return;
    }
    await getMe();
    setNotifLoading(false);
  };

  return (
    <motion.div
      className="map-hud-top"
      initial={isMobile ? undefined : { y: -120, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
    >
      {/* Avatar -> opens the profile / settings dialog directly */}
      <div
        className="mht-avatar"
        onClick={openSettings}
        role="button"
        {...hoverSfx("topbar")}
      >
        {userInfo?.photoFile && (
          <img
            className="mht-photo"
            alt={t("navigation:avatar")}
            src={`${ProfileURL}/${userInfo?.id}/${userInfo?.photoFile}`}
          />
        )}
      </div>

      <div className="mht-stack">
        {/* Resource toolbar — the whole bar opens the wallet modal */}
        <div
          className="mht-resources"
          role="button"
          tabIndex={0}
          aria-label={t("common:resources.open")}
          {...hoverSfx("resource")}
          onPointerDown={onBarPointerDown}
          onPointerMove={onBarPointerMove}
          onClick={onBarClick}
          onKeyDown={(e) => {
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault(); // Space would otherwise scroll the page
            setResourcesOpen(true);
          }}
        >
          <img
            className="mht-bar-cap mht-bar-cap--start"
            src="/assets/images/bar-start.png"
            alt=""
          />
          <div className="mht-res-strip">
            <div className="mht-res-track">
              {resources.map((r) => (
                <div
                  className={"mht-pill" + (r.gold ? " mht-pill--gold" : "")}
                  key={r.key}
                >
                  <img src={r.icon} alt={t(r.label)} />
                  <span>{compact(r.value)}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Chevron affordance — pinned outside the scroll area so it (and the
              bar's shaped right end) stay on screen on narrow phones. */}
          <span className="mht-res-more" aria-hidden="true">
            <img src="/assets/images/hud/scroll-arrow.svg" alt="" />
          </span>
          <img
            className="mht-bar-cap mht-bar-cap--end"
            src="/assets/images/bar-end.png"
            alt=""
          />
        </div>

        {/* Rank banner + settings + notifications */}
        <div className="mht-row2">
          <Link to="/ranking" className="mht-rank" {...hoverSfx("topbar")}>
            <img className="mht-crown" src="/assets/images/hud/rank-crown.png" alt={t("navigation:ranking")} />
            <span>{ThousandsSeparator(userInfo?.rank ?? 0)}</span>
          </Link>

          <button
            className="mht-icon-btn"
            aria-label={t("navigation:settings")}
            onClick={() => toggleDrawer()}
            {...hoverSfx("topbar")}
          >
            <img src="/assets/images/hud/gear.svg" alt="" />
          </button>

          <Link
            to="/notifications"
            className="mht-icon-btn"
            aria-label={t("navigation:notifications")}
            {...hoverSfx("topbar")}
          >
            <img src="/assets/images/hud/bell.svg" alt="" />
            {unreadNotifications ? (
              <img className="mht-reddot" src="/assets/images/hud/reddot.svg" alt="" />
            ) : null}
          </Link>

          {isThereNotif && (
            <div className="mht-ticker">
              {notifLoading ? (
                <CircularProgress style={{ height: 18, width: 18 }} />
              ) : (
                <span
                  onClick={() => markAsRead(userInfo?.newNotification?.id, true)}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(userInfo?.newNotification?.message || "", {
                      ALLOWED_TAGS: ["a", "b", "span"],
                    }),
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>

      <ResourceCoachMark disabled={resourcesOpen} />
      <ResourcesDialog
        open={resourcesOpen}
        onClose={() => setResourcesOpen(false)}
      />
    </motion.div>
  );
};

export default MapTopBar;
